/**
 * Geo resolver — the ONE accessor over the canonical bundle (india-geo.json) for both the server
 * (register-time validation) and the client (cascading picker options + name→id resolution).
 *
 * PHASE-1b: cities are STATE-scoped (districtId is null in the 1a dataset), so the cascade is
 * state→district (real) and state→city (state-scoped, with a free-text fallback for unlisted towns).
 */
import geo from './india-geo.json'

export interface GeoState { id: string; code: string; name: string }
export interface GeoDistrict { id: string; name: string; stateId: string }
export interface GeoCity { id: string; name: string; stateId: string; districtId: string | null }

export const geoStates = geo.states as GeoState[];
export const geoDistricts = geo.districts as GeoDistrict[];
export const geoCities = geo.cities as GeoCity[];

const stateById = new Map(geoStates.map((s) => [s.id, s]));
const stateByName = new Map(geoStates.map((s) => [s.name.toLowerCase(), s]));
const districtById = new Map(geoDistricts.map((d) => [d.id, d]));
const cityById = new Map(geoCities.map((c) => [c.id, c]));

/** Districts for a state (real cascade). */
export function districtsForState(stateId: string): GeoDistrict[] {
  return geoDistricts.filter((d) => d.stateId === stateId);
}
/** Cities for a state — STATE-scoped (not district; districtId is null in 1a). */
export function citiesForState(stateId: string): GeoCity[] {
  return geoCities.filter((c) => c.stateId === stateId);
}

/** Resolve a state NAME (the label the <select> carries) → its id, or null. */
export function stateIdByName(name: string): string | null {
  return stateByName.get(String(name || '').trim().toLowerCase())?.id ?? null;
}
/** Resolve a typed district name within a state → its id, or null (free-text → null). */
export function districtIdByName(stateId: string | null, name: string): string | null {
  if (!stateId) return null;
  const n = String(name || '').trim().toLowerCase();
  return districtsForState(stateId).find((d) => d.name.toLowerCase() === n)?.id ?? null;
}
/** Resolve a typed city name within a state → its id, or null (free-text town → null). */
export function cityIdByName(stateId: string | null, name: string): string | null {
  if (!stateId) return null;
  const n = String(name || '').trim().toLowerCase();
  return citiesForState(stateId).find((c) => c.name.toLowerCase() === n)?.id ?? null;
}

/**
 * SERVER validation. Accepts whatever ids the client sent and returns only the ones that are real +
 * parent-consistent; everything else → null (NEVER throws, NEVER hard-fails a signup). A districtId
 * counts only if it exists AND belongs to the resolved state; a cityId only if it exists AND belongs
 * to the resolved state. If stateId is invalid/absent, all three resolve to null.
 */
export function resolveGeoIds(input: { stateId?: unknown; districtId?: unknown; cityId?: unknown }): {
  stateId: string | null; districtId: string | null; cityId: string | null;
} {
  const sid = typeof input.stateId === 'string' ? input.stateId : null;
  const did = typeof input.districtId === 'string' ? input.districtId : null;
  const cid = typeof input.cityId === 'string' ? input.cityId : null;

  const state = sid && stateById.has(sid) ? sid : null;
  const district = state && did && districtById.get(did)?.stateId === state ? did : null;
  const city = state && cid && cityById.get(cid)?.stateId === state ? cid : null;
  return { stateId: state, districtId: district, cityId: city };
}
